import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AvailableAccount } from "src/app/accounts/AvailableAccount";
import {
  confirmPasswordValidator,
  passwordValidator,
} from "src/app/auth/auth.validators";
import { UserDTO } from "src/app/auth/User";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";
import { UsersService } from "../users.service";

@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
  styleUrls: ["./change-password.component.scss"],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  changePasswordForm!: FormGroup;
  private onComponentDestroy = new Subject();

  constructor(
    private router: Router,
    private userService: UsersService,
    private notificationService: NotificationService,
    private sessionService: SessionService,
    private uiService: UiService,
  ) {}

  get currentUser(): UserDTO {
    return this.sessionService.currentUserValue!;
  }

  get currentAccount(): AvailableAccount {
    return this.sessionService.currentAccountValue!;
  }

  ngOnInit(): void {
    this.changePasswordForm = new FormGroup({
      currentPassword: new FormControl("", [Validators.required]),
      newPassword: new FormControl("", [
        Validators.required,
        passwordValidator(),
      ]),
    });

    this.changePasswordForm.addControl(
      "confirmNewPassword",
      new FormControl("", [
        Validators.required,
        confirmPasswordValidator(this.changePasswordForm.controls.newPassword),
      ]),
    );

    // On newPassword change, check the validity of the currentPassword
    this.changePasswordForm.controls.newPassword.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        this.changePasswordForm.controls.confirmNewPassword.updateValueAndValidity();
      });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  changePassword() {
    this.uiService
      .waitForEvent(
        this.userService.changePassword(
          this.currentAccount.accountID,
          {
            username: this.currentUser.email,
            password: this.changePasswordForm.controls.currentPassword.value,
            newPassword: this.changePasswordForm.controls.newPassword.value,
          },
          this.currentUser,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.router
            .navigate(["users", this.currentUser.id, "preferences"])
            .then(() => {
              this.notificationService.openSuccessSnackBar(
                "Password changed successfully",
              );
            });
        },
        (err) => {
          this.notificationService.openErrorSnackBar(err.error.message);
        },
      );
  }

  getErrorTooltip(abstractControl: AbstractControl) {
    let firstError = "";
    if (abstractControl.errors) {
      // Return first error of control

      for (const key of Object.keys(abstractControl.errors)) {
        // TODO: Clean up validations into its own directive
        if (key === "required") {
          firstError = "This field is required";
        } else {
          firstError = abstractControl.errors?.[key];
        }
        break;
      }
    }

    return firstError;
  }
}
